import React, { useState, useEffect } from 'react';
import { 
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui';
import { useToast } from '../../lib/hooks/useToast';
import {
  Play,
  Edit,
  Trash2,
  Calendar,
  FileText,
  AlertCircle,
  CheckCircle,
  Clock,
  RefreshCw,
  Plus,
  Upload,
  Stamp
} from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { EnhancedInvoiceModal } from './EnhancedInvoiceModal';
import { enhancedInvoiceQueueService } from '../../lib/services/enhancedInvoiceQueueService';
import type { EnhancedInvoiceQueueItem } from '../../lib/types/fattureincloud';
import { formatCurrency } from '../../lib/utils';

interface EnhancedInvoiceQueueProps {
  dealId: string;
}

type QueueStatus = 'all' | 'pending' | 'processing' | 'completed' | 'failed';

export function EnhancedInvoiceQueue({ dealId }: EnhancedInvoiceQueueProps) {
  const toast = useToast();
  
  // State
  const [queueItems, setQueueItems] = useState<EnhancedInvoiceQueueItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<Set<string>>(new Set());
  const [selectedItem, setSelectedItem] = useState<EnhancedInvoiceQueueItem | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [statusFilter, setStatusFilter] = useState<QueueStatus>('all');

  // Load queue items
  const loadQueueItems = async () => {
    try {
      setLoading(true);
      const items = await enhancedInvoiceQueueService.getQueueItemsForDeal(dealId);
      setQueueItems(items);
    } catch (error) {
      console.error('Error loading queue items:', error);
      toast.error('Errore nel caricamento della coda fatture');
    } finally {
      setLoading(false);
    }
  };

  // Load items on mount
  useEffect(() => {
    if (dealId) {
      loadQueueItems();
    }
  }, [dealId]);

  // Get status info
  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'pending':
        return {
          label: 'In attesa',
          variant: 'secondary' as const,
          icon: Clock
        };
      case 'processing':
        return {
          label: 'Elaborazione',
          variant: 'default' as const,
          icon: RefreshCw
        };
      case 'completed':
        return {
          label: 'Completata',
          variant: 'success' as const,
          icon: CheckCircle
        };
      case 'failed':
        return {
          label: 'Fallita',
          variant: 'destructive' as const,
          icon: AlertCircle
        };
      default:
        return {
          label: status,
          variant: 'secondary' as const,
          icon: Clock
        };
    }
  };

  // Filter items by status
  const filteredItems = queueItems.filter(item => {
    if (statusFilter === 'all') return true;
    return item.status === statusFilter;
  });

  // Process queue item
  const handleProcessItem = async (itemId: string) => {
    try {
      setProcessing(prev => new Set(prev).add(itemId));
      
      const result = await enhancedInvoiceQueueService.processQueueItem(itemId);
      
      if (result.success) {
        toast.success('Fattura inviata a Fatture in Cloud con successo');
      } else {
        toast.error(result.error || 'Errore durante l\'elaborazione');
      }
      
      // Reload items
      await loadQueueItems();
    } catch (error) {
      console.error('Error processing item:', error);
      toast.error('Errore durante l\'elaborazione della fattura');
    } finally {
      setProcessing(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    }
  };

  // Edit queue item
  const handleEditItem = (item: EnhancedInvoiceQueueItem) => {
    setSelectedItem(item);
    setModalMode('edit');
    setShowModal(true);
  };

  // Delete queue item
  const handleDeleteItem = async (itemId: string) => {
    if (!confirm('Sei sicuro di voler eliminare questo elemento dalla coda?')) {
      return;
    }

    try {
      await enhancedInvoiceQueueService.deleteQueueItem(itemId);
      toast.success('Elemento rimosso dalla coda');
      await loadQueueItems();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Errore durante l\'eliminazione');
    }
  };

  // Create new queue item
  const handleCreateItem = () => {
    setSelectedItem(null);
    setModalMode('create');
    setShowModal(true);
  };

  // Generate queue items for deal
  const handleGenerateQueueItems = async () => {
    try {
      setLoading(true);
      const itemsCreated = await enhancedInvoiceQueueService.generateEnhancedQueueItemsForDeal(dealId);
      
      if (itemsCreated > 0) {
        toast.success(`Generate ${itemsCreated} elementi nella coda fatture`);
        await loadQueueItems();
      } else {
        toast.info('Nessun elemento generato (controlla le impostazioni dei servizi)');
      }
    } catch (error) {
      console.error('Error generating queue items:', error);
      toast.error('Errore durante la generazione degli elementi');
    } finally {
      setLoading(false);
    }
  };

  // Save modal data
  const handleSaveModal = async (data: any) => {
    try {
      if (modalMode === 'edit' && selectedItem) {
        await enhancedInvoiceQueueService.updateQueueItem(selectedItem.id, data);
        toast.success('Elemento aggiornato con successo');
      } else {
        // For create mode, you would implement creation logic here
        toast.info('Funzionalità di creazione manuale in sviluppo');
      }

      await loadQueueItems();
      setShowModal(false);
    } catch (error) {
      console.error('Error saving item:', error);
      throw error;
    }
  };

  // Get status counts
  const statusCounts = queueItems.reduce((acc, item) => {
    acc[item.status] = (acc[item.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Coda fatturazione avanzata
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={loadQueueItems}
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Aggiorna
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleGenerateQueueItems}
              >
                <Upload className="h-4 w-4 mr-2" />
                Genera da offerta
              </Button>
              <Button
                size="sm"
                onClick={handleCreateItem}
              >
                <Plus className="h-4 w-4 mr-2" />
                Nuova fattura
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Status Summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div
              className={`p-4 rounded-lg cursor-pointer transition-colors ${
                statusFilter === 'all' ? 'bg-blue-100 dark:bg-blue-900/50 border-blue-300 dark:border-blue-700' : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-700/50 dark:hover:bg-gray-700'
              }`}
              onClick={() => setStatusFilter('all')}
            >
              <div className="text-2xl font-bold dark:text-white">{queueItems.length}</div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Totali</div>
            </div>
            <div
              className={`p-4 rounded-lg cursor-pointer transition-colors ${
                statusFilter === 'pending' ? 'bg-blue-100 dark:bg-blue-900/50 border-blue-300 dark:border-blue-700' : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-700/50 dark:hover:bg-gray-700'
              }`}
              onClick={() => setStatusFilter('pending')}
            >
              <div className="text-2xl font-bold text-gray-600 dark:text-gray-300">{statusCounts.pending || 0}</div>
              <div className="text-sm text-gray-600 dark:text-gray-300">In attesa</div>
            </div>
            <div
              className={`p-4 rounded-lg cursor-pointer transition-colors ${
                statusFilter === 'processing' ? 'bg-blue-100 dark:bg-blue-900/50 border-blue-300 dark:border-blue-700' : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-700/50 dark:hover:bg-gray-700'
              }`}
              onClick={() => setStatusFilter('processing')}
            >
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{statusCounts.processing || 0}</div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Elaborazione</div>
            </div>
            <div
              className={`p-4 rounded-lg cursor-pointer transition-colors ${
                statusFilter === 'completed' ? 'bg-blue-100 dark:bg-blue-900/50 border-blue-300 dark:border-blue-700' : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-700/50 dark:hover:bg-gray-700'
              }`}
              onClick={() => setStatusFilter('completed')}
            >
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">{statusCounts.completed || 0}</div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Completate</div>
            </div>
            <div
              className={`p-4 rounded-lg cursor-pointer transition-colors ${
                statusFilter === 'failed' ? 'bg-blue-100 dark:bg-blue-900/50 border-blue-300 dark:border-blue-700' : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-700/50 dark:hover:bg-gray-700'
              }`}
              onClick={() => setStatusFilter('failed')}
            >
              <div className="text-2xl font-bold text-red-600 dark:text-red-400">{statusCounts.failed || 0}</div>
              <div className="text-sm text-gray-600 dark:text-gray-300">Fallite</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Queue Items Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            Elementi in coda {statusFilter !== 'all' && `(${getStatusInfo(statusFilter).label})`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8 dark:text-white">
              <RefreshCw className="h-6 w-6 animate-spin mr-2" />
              Caricamento...
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              {statusFilter === 'all' ? 'Nessun elemento in coda' : `Nessun elemento ${getStatusInfo(statusFilter).label.toLowerCase()}`}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Descrizione</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Importo</TableHead>
                  <TableHead>Scadenza</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Tentativi</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map((item) => {
                  const statusInfo = getStatusInfo(item.status);
                  const StatusIcon = statusInfo.icon;
                  
                  return (
                    <TableRow key={item.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium dark:text-white">
                            {item.installment_description || item.fic_data.subject || 'Fattura'}
                          </div>
                          {item.fic_data.items && item.fic_data.items.length > 0 && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {item.fic_data.items[0].name}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm dark:text-white">
                          {item.fic_data.entity?.name || 'N/A'}
                          {item.fic_data.entity?.vat_number && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              P.IVA: {item.fic_data.entity.vat_number}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium dark:text-white">
                          {formatCurrency(item.amount)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                          <span className="text-sm dark:text-white">
                            {format(new Date(item.due_date), 'dd MMM yyyy', { locale: it })}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusInfo.variant} className="flex items-center gap-1">
                          <StatusIcon className="h-3 w-3" />
                          {statusInfo.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {/* Badge Tipo Documento */}
                          <Badge variant="outline">
                            {item.fic_data.type === 'invoice' ? 'Fattura' :
                             item.fic_data.type === 'quote' ? 'Preventivo' :
                             item.fic_data.type === 'proforma' ? 'Proforma' :
                             item.fic_data.type}
                          </Badge>

                          {/* Badge Marca da Bollo */}
                          {item.fic_data.items?.[0]?.category === 'Bollo' && (
                            <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-300">
                              <Stamp className="h-3 w-3 mr-1" />
                              Bollo
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {item.retry_count > 0 && (
                          <span className="text-sm text-orange-600 dark:text-orange-400">
                            {item.retry_count}/{item.max_retries || 3}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {item.status === 'pending' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditItem(item)}
                                title="Modifica"
                              >
                                <Edit className="h-4 w-4 text-blue-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleProcessItem(item.id)}
                                disabled={processing.has(item.id)}
                                title="Invia a Fatture in Cloud"
                              >
                                {processing.has(item.id) ? (
                                  <RefreshCw className="h-4 w-4 animate-spin text-green-600" />
                                ) : (
                                  <Play className="h-4 w-4 text-green-600" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteItem(item.id)}
                                title="Elimina"
                              >
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            </>
                          )}
                          {item.status === 'failed' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditItem(item)}
                                title="Modifica e riprova"
                              >
                                <Edit className="h-4 w-4 text-blue-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleProcessItem(item.id)}
                                disabled={processing.has(item.id)}
                                title="Riprova invio"
                              >
                                {processing.has(item.id) ? (
                                  <RefreshCw className="h-4 w-4 animate-spin text-orange-600" />
                                ) : (
                                  <RefreshCw className="h-4 w-4 text-orange-600" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteItem(item.id)}
                                title="Elimina"
                              >
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            </>
                          )}
                          {item.status === 'completed' && (
                            <span className="text-sm text-gray-400">Completata</span>
                          )}
                          {item.status === 'processing' && (
                            <RefreshCw className="h-4 w-4 animate-spin text-blue-600" />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Enhanced Invoice Modal */}
      <EnhancedInvoiceModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        dealId={dealId}
        queueItem={selectedItem}
        queueItemId={selectedItem?.id}
        initialData={selectedItem?.fic_data}
        onSave={handleSaveModal}
        mode={modalMode}
      />
    </div>
  );
}